/* eslint-disable array-bracket-newline */
import { IDexTrade, Network } from '@kattana-io/common';
import { useMemo } from 'react';
import { connect } from 'react-redux';
import { actionShowModal } from '../../../actions/modal';
import { actionCreateNotification } from '../../../actions/notifications';
import { IStore } from '../../../reducers';
import { ModalTypes } from '../../../reducers/modal';
import { switchCurrentTicker } from '../../../Authlogic';
import { Loader } from '../../DexTrades';
import useCollectTickers from '../../DexTrades/useCollectTickers';
import useWalletInfo from './useWalletInfo';
import StatsTable from './StatsTable';
import TradesTable from './TradesTable';
import TradesTableMobile from './mobile/TradesTable';
import ErrorPlaceholder from '../ErrorPlaceholder';
import NoDataPlaceholder from '../NoDataPlaceholder';
import useDevice from '../../../pages/MainPage/useMobileDetect';

import './styles.scss';

interface IWalletModalProps {
  actionCloseModal: () => void;
  actionShowModal: (type: ModalTypes, props: any) => void;
  actionCreateNotification: (input: any) => void;
  modalProps: any;
}

interface IPriceStats {
  avg: number;
  max: number;
  min: number;
}

interface INullablePriceStats {
  avg: number | null;
  max: number | null;
  min: number | null;
}

export interface IStats {
  first_trade: string;
  buy_trades_count: number;
  bot_trades_count: number;
  sell_trades_count: number;
  buy_price: IPriceStats;
  buy_price_usd: INullablePriceStats;
  sell_price: IPriceStats;
  sell_price_usd: INullablePriceStats;
}

export interface ITrades {
  amount0: string;
  amount1: string;
  blocknumber: number;
  bot: number;
  buy: number;
  format_date: string;
  order: number;
  pair: string;
  price: number;
  price_usd: number;
  protocol: string;
  sell: number;
  tx: string;
  wallet: string;
}

export const getChainsArrayFromTradesArray = (trades: IDexTrade[]) => {
  const protocolsSet = new Set([
    ...trades.map((item: IDexTrade) => item.chain),
  ]);

  const protocolsArr = Array.from(protocolsSet);
  return protocolsArr;
};

const WalletModal = ({
  actionCloseModal,
  actionShowModal,
  actionCreateNotification,
  modalProps,
}: IWalletModalProps) => {
  const { wallet, address, timeframe, history, chain } = modalProps;

  const { isMobile } = useDevice();
  const network = useMemo(() => Network.fromChain(chain), [chain]);

  const collection = useMemo(() => {
    return [{ chain, pair: address }];
  }, [chain, address]);

  const { tickers, loadingTickers } = useCollectTickers(collection);

  const { isErrorNoTrades, isServerError, trades, stats, loading } =
    useWalletInfo({ address, chain, wallet, timeframe });

  const ticker = tickers[`${chain}/${address}`];

  const [base, quote] = ticker?.name.split('/') || ['', ''];

  const calcVolume = (trades: IDexTrade[]) =>
    trades.reduce((cons, trade) => cons + +trade.value_usd, 0);

  const calcProfit = (trades: IDexTrade[]) =>
    trades.reduce((prev: number, trade: IDexTrade) => {
      if (trade.buy) {
        return prev - Number(trade.value_usd);
      } else {
        return prev + Number(trade.value_usd);
      }
    }, 0);

  const onShare = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/top-traders/${chain}/${address}/${wallet}/${timeframe}`
    );
    actionCreateNotification({ message: 'Link copied to clipboard' });
  };

  const onScan = () => {
    const link = network?.getScanForWallet() || '';
    window.open(`${link}${wallet}`);
  };

  const onWalletClick = () => {
    actionShowModal(ModalTypes.walletPairs, {
      wallet,
      timeframe,
      history: history ? [modalProps, ...history] : [modalProps],
    });
  };

  const onBackClick = () => {
    const goBack = modalProps.history[0];
    actionShowModal(ModalTypes.walletPairs, goBack);
  };

  const onPairNameClick = () =>
    switchCurrentTicker({
      address,
      chain,
    });

  if (loading) {
    return (
      <div className='wallet_modal'>
        <Loader />
      </div>
    );
  }
  const isValid = Object.keys(stats).length > 0 && trades.length > 0;
  return (
    <>
      <div className='wallet_modal'>
        <div className='wallet_modal__header'>
          {modalProps.history && (
            <div className='wallet_modal__back' onClick={onBackClick} />
          )}
          <span>
            <div className='wallet_modal__wallet__title'>{wallet}</div>
            &nbsp;on&nbsp;
            <span
              onClick={onPairNameClick}
              className='wallet_modal__wallet__pair_name'
            >
              {base}/{quote}
            </span>
          </span>
          <div className='wallet_modal__header__actions'>
            <span
              className='wallet_modal__pill wallet_modal__share'
              onClick={onShare}
            >
              Share
            </span>
            <span
              className='wallet_modal__pill wallet_modal__scan'
              onClick={onScan}
            >
              Scan
            </span>
          </div>
        </div>
        <div className='wallet_modal__close' onClick={actionCloseModal} />
        <div className='wallet_modal__container'>
          {loading || loadingTickers ? (
            <Loader />
          ) : (
            <>
              {isServerError && <ErrorPlaceholder />}
              {(isErrorNoTrades || !isValid) && <NoDataPlaceholder />}
              {isValid && !isServerError && !isErrorNoTrades && (
                <StatsTable
                  stats={stats as IStats}
                  base={base}
                  quote={quote}
                  ticker={ticker}
                  wallet={wallet}
                  volume={calcVolume(trades)}
                  profit={calcProfit(trades)}
                />
              )}
            </>
          )}

          {!loading && !loadingTickers && isValid && (
            <>
              {isMobile ? (
                <TradesTableMobile
                  flipped={ticker?.flipped}
                  base={base}
                  quote={quote}
                  trades={trades}
                  base_decimals={ticker.base_decimals}
                  quote_decimals={ticker.quote_decimals}
                  chain={ticker.chain}
                />
              ) : (
                <div className='trades__table table_desktop__container'>
                  <TradesTable
                    flipped={ticker.flipped}
                    base={base}
                    quote={quote}
                    trades={trades}
                    base_decimals={ticker.base_decimals}
                    quote_decimals={ticker.quote_decimals}
                    chain={ticker.chain}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className='wallet_modal__backdrop' onClick={actionCloseModal} />
    </>
  );
};

const mapStateToProps = (state: IStore) => ({
  modalProps: state.modal.props,
});

export default connect(mapStateToProps, {
  actionShowModal,
  actionCreateNotification,
})(WalletModal);
